<template>
  <v-list-item
    :key="r.id"
    class="pa-0"
    :value="r.id"
  >
    <cancel-reservation-dialog
      v-if="confirmCancelReservation"
      :reservation="reservation"
      @refund-reservation="cancelReservation(true)"
      @cancel-reservation="cancelReservation"
      @close="confirmCancelReservation=false"
    />
    <v-list-item-content class="mh-48 py-1 pl-0 pr-2">
      <v-lazy class="w-100">
        <!-- Compact View -->
        <v-row
          v-if="compact"
          class="pa-0 ma-0 pl-0 w-100"
        >
          <v-col class="pa-0 ma-0">
            <v-row class="pa-0 ma-0 d-flex align-center">
              <v-col
                class="pa-0 ma-0 flex-grow-0 flex-shrink-1"
                :style="`min-width: ${searchResult ? 160 : 70}px`"
              >
                <!-- Icon Column -->
                <div
                  v-if="!r.isBlock"
                  class="glr-icon-column pa-0 ma-0"
                >
                  <!-- Flagged Flag -->
                  <v-icon
                    v-if="r.isFlagged"
                    class="pl-1 pa-0"
                    x-small
                    color="yellow"
                  >
                    mdi-circle
                  </v-icon>
                  <!-- Unread Flag -->
                  <v-btn
                    v-if="unreadReservations && r.isUnread"
                    icon
                    x-small
                    @click.stop="markAsRead"
                    @mousedown.stop
                    @touchstart.native.stop
                  >
                    <v-icon
                      x-small
                      color="brand"
                    >
                      mdi-circle
                    </v-icon>
                  </v-btn>
                </div>
                <!-- /// -->

                <!--  IF SEARCH RESULT: Show Starting Time -->
                <span
                  v-if="searchResult"
                  class="glr-search-time font-weight-medium pr-5"
                >{{ $localized.hourMinText(r.dateBegin) }}</span>
                <!-- /// -->
                <!-- Party Size -->
                <div
                  v-if="!r.isBlock"
                  class="glr-partysize-compact"
                >
                  <span class="font-weight-medium">
                    {{ r.partySizeText }}</span><span class="text-caption">p.</span>
                </div>
                <div
                  v-else
                  style="margin-left: 20px; min-width: 250px; display: inline-block"
                >
                  <span class="font-weight-medium">{{ $t('label.table_blocked') }}</span>
                <!-- /// -->
                </div>
              </v-col>

              <!-- Tab Item -->
              <v-col
                :class="[
                  'pa-0 ma-0 text-right flex-grow-1 flex-shrink-0 text-body-2 ellipsize',
                  {'error--text': r.tabItems.length === 0}]"
              >
                {{ r.tabText }}
                <template v-if="r.tabItems.length > 0">
                  <!-- this is safe. String is defined by user, not displayed to other users. plus it's short -->
                  <!-- eslint-disable vue/no-v-html -->
                  - <span
                    class="font-weight-bold"
                    v-html="tablesHtmlText"
                  />
                  <!-- eslint-enable -->
                </template>
              </v-col>
            <!-- /// -->
            </v-row>

            <!-- Name -->
            <v-row class="pa-0 ma-0 d-flex align-center">
              <v-col
                v-if="!r.isBlock"
                class="pa-0 ma-0 flex-grow-1 flex-shrink-1 ellipsize"
              >
                <div class="glr-star-compact pa-0 ma-0">
                  <v-icon
                    v-if="r.isStarred"
                    color="primary"
                    style="padding-left: 3px!important; padding-bottom:3px;"
                    small
                  >
                    mdi-star
                  </v-icon>
                </div>
                <div
                  v-if="r.contact.isVIP"
                  class="glr-star-compact pa-0 ma-0"
                >
                  <v-icon
                    color="primary"
                    style="padding-bottom:3px;"
                    small
                  >
                    mdi-crown
                  </v-icon>
                </div>
                <span
                  v-if="r.nameText"
                  class="font-weight-medium text-subtitle-1"
                >{{ r.nameText }}</span>
                <span
                  v-else
                  class="text--secondary text-subtitle-1"
                >{{ isWalkin ? $t('code.booking_type.walkin') : $t('placeholder.no_name') }}</span>
                <sup
                  v-if="r.contact.reservationCount !== 0"
                  class="pl-1"
                >{{ r.contact.reservationCount }}</sup>
                <div
                  v-if="r.contact.companyName"
                  class="ml-5 mb-1 glr-compact-company text-body-2 text--secondary"
                >
                  {{ r.contact.companyName }}
                </div>
              </v-col>
              <!-- /// -->

              <!-- Status Button -->
              <v-col
                v-if="!r.isBlock"
                class="glr-block-125 pa-0 ma-0 text-right"
              >
                <!-- Fake status button for search results -->
                <v-btn
                  v-if="searchResult"
                  depressed
                  :outlined="statusButtonOutlined(r.status)"
                  style="min-width:125px"
                  small
                  :color="colorStatusButton"
                >
                  {{ $t(`code.status.${r.statusText}`) }}
                </v-btn>
                <status-selection-menu
                  v-else
                  :value="r.status"
                  :reservation="r"
                  @item-clicked="statusChanged"
                >
                  <template #activator="{ on: { click } }">
                    <hideable-badge
                      color="purple darken-3"
                      :content="waitlistBadgeContent"
                      :visible="waitlistBadgeVisible"
                    >
                      <v-btn
                        depressed
                        :outlined="statusButtonOutlined(r.status)"
                        style="min-width:125px"
                        small
                        :color="colorStatusButton"
                        @click.stop="click"
                        @mousedown.stop
                        @touchstart.native.stop
                      >
                        {{ $t(`code.status.${r.statusText}`) }}
                      </v-btn>
                    </hideable-badge>
                  </template>
                </status-selection-menu>
              </v-col>
              <!-- Chips -->
              <v-col
                cols="12"
                class="glr-chips-compact pa-0 ma-0 text-caption"
              >
                <reservation-chips
                  compact
                  :reservation="r"
                />
              </v-col>
              <!-- Reservation Note -->
              <v-col
                v-if="r.notesText"
                cols="12"
                class="glr-note-compact pa-0 ma-0 text-caption"
              >
                <span
                  class="ellipsize d-table-cell"
                >{{ r.notesText }}</span>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <!--  -->
        <!-- Full View -->
        <!--  -->
        <v-row
          v-else
          class="pl-2 pa-0 ma-0 d-flex align-center"
          style="max-width: 100%"
        >
          <!-- FLAGGED -->
          <v-col class="glr-block-20 pa-0 ma-0 ml-1 text-center">
            <v-icon
              v-if="r.isFlagged"
              class="pr-0"
              style="margin-bottom: 1px"
              x-small
              color="yellow"
            >
              mdi-circle
            </v-icon>
            <v-btn
              v-if="unreadReservations && r.isUnread"
              icon
              x-small
              @click.stop="markAsRead"
              @mousedown.stop
              @touchstart.native.stop
            >
              <v-icon
                x-small
                color="brand"
              >
                mdi-circle
              </v-icon>
            </v-btn>
            <v-icon
              v-if="r.isStarred"
              small
              color="primary"
            >
              mdi-star
            </v-icon>
          </v-col>
          <!-- PARTYSIZE -->
          <v-col class="glr-block-50 pa-0 ma-0 text-right">
            <span class="text-h6 font-weight-medium">{{ r.partySizeText }}
              <span class="text-body-2"> p.</span>
            </span>
          </v-col>
          <!-- NAME -->
          <v-col
            class="pa-0 flex-grow-1 flex-shrink-1 mx-3"
            style="max-width: 200px"
            align-self="center"
          >
            <v-icon
              v-if="r.contact.isVIP"
              small
            >
              mdi-crown
            </v-icon>
            <span
              v-if="r.nameText"
              class="valign-middle font-weight-medium text-h7"
            >
              {{ r.nameText }}
            </span>
            <span
              v-else
              class="valign-middle text-h7 text--secondary"
            >{{ isWalkin ? $t('code.booking_type.walkin') : $t('placeholder.no_name') }}</span>
            <sup v-if="r.contact.reservationCount ?? 0 > 0">{{ r.contact.reservationCount }}</sup>
            <div
              v-if="r.contact.companyName"
              class="py-0 text-body-2 text--secondary"
            >
              {{ r.contact.companyName }}
            </div>
          </v-col>
          <!-- NOTE -->
          <v-col
            class="pa-0 text-body-2 text-left flex-grow-1 flex-shrink-1"
            style="max-height: 40px; overflow: hidden;"
          >
            <div class="text-left">
              <reservation-chips :reservation="r" />
            </div>
            <v-tooltip
              bottom
              open-delay="400"
              max-width="80%"
            >
              <template #activator="{on, attrs}">
                <div
                  class="text-left ellipsize"
                  v-bind="attrs"
                  v-on="on"
                >
                  {{ r.notesText }}
                </div>
              </template>
              <span class="text-pre-wrap">{{ r.notesText }}</span>
            </v-tooltip>
          </v-col>

          <v-col
            :class="[
              'pa-0 text-body-2 text-center ml-3 mr-3 glr-name',
              {'glr-name-compact': $vuetify.breakpoint.lgAndDown}]"
          >
            <div class="text-center font-weight-bold ellipsize">
              <!-- this is safe. String is defined by user, not displayed to other users. plus it's short -->
              <!-- eslint-disable-next-line vue/no-v-html -->
              <span v-html="tablesHtmlText" />
            </div>
            <div :class="[{'error--text': r.tabItems.length === 0}, 'text-center', 'ellipsize']">
              {{ r.tabText }}
            </div>
          </v-col>
          <!-- STATUS BUTTON -->
          <v-col class="glr-status-btn pr-3 pa-0 ma-0 align-center">
            <status-selection-menu
              :value="r.status"
              :reservation="r"
              @item-clicked="statusChanged"
            >
              <template #activator="{ on: { click } }">
                <hideable-badge
                  color="purple darken-4"
                  :content="waitlistBadgeContent"
                  :visible="waitlistBadgeVisible"
                >
                  <v-btn
                    width="150"
                    depressed
                    small
                    :outlined="statusButtonOutlined(r.status)"
                    :color="colorStatusButton"
                    @click.stop="click"
                    @mousedown.stop
                    @touchstart.native.stop
                  >
                    {{ $t(`code.status.${r.statusText}`) }}
                  </v-btn>
                </hideable-badge>
              </template>
            </status-selection-menu>
          </v-col>
        </v-row>
      </v-lazy>
    </v-list-item-content>
  </v-list-item>
</template>

<script lang='ts'>
import Vue, { PropType } from 'vue';
import StatusSelectionMenu from '@/components/views/guests/StatusSelectionMenu.vue';
import Reservation, { ReservationStatus, ReservationType } from '@/model/Reservation';
import ReservationChips from '@/components/views/guests/ReservationChips.vue';
import { mapActions } from 'vuex';
import { performSaveAction } from '@/services/vue-utils';
import { cloneModel, toMapById } from '@/model/model-utils';
import HideableBadge from '@/components/util/HideableBadge.vue';
import CancelReservationDialog from '@/components/dialogs/CancelReservationDialog.vue';

export default Vue.extend({
  name: 'GuestListRow',
  components: {
    StatusSelectionMenu, ReservationChips, HideableBadge, CancelReservationDialog,
  },
  props: {
    reservation: { type: Object as PropType<Reservation>, required: true },
    waitlistListOrder: { type: Number as PropType<number>, required: false, default: 0 },
    overbookedTabItems: { type: Set as PropType<Set<number>|undefined>, required: false, default: undefined },
    compact: { type: Boolean, required: false, default: false },
    searchResult: { type: Boolean, required: false, default: false },
    unreadReservations: { type: Boolean, required: false, default: false },
  },
  data() {
    return {
      confirmCancelReservation: false,
    };
  },
  computed: {
    r(): Reservation {
      return this.reservation;
    },
    isWalkin(): boolean {
      return this.r.bookingType === ReservationType.Walkin;
    },
    waitlistBadgeContent(): string | null {
      return this.waitlistListOrder.toString();
    },
    waitlistBadgeVisible(): boolean {
      return this.r.status === ReservationStatus.WaitingList;
    },
    colorStatusButton(): string {
      switch (this.r.status) {
        case ReservationStatus.Planned: return 'grey lighten-1';
        case ReservationStatus.Confirmed: return 'info white--text';
        case ReservationStatus.CheckedIn: return 'orange white--text';
        case ReservationStatus.CheckedOut: return 'grey-50 lighten-2 white--text';
        case ReservationStatus.Seated: return 'green white--text';
        case ReservationStatus.NonConfirmed: return 'purple white--text';
        case ReservationStatus.Cancelled: return 'red white--text';
        case ReservationStatus.Spam: return 'red white--text';
        case ReservationStatus.Deleted: return 'red white--text';
        case ReservationStatus.NoShow: return 'red white--text';
        case ReservationStatus.WaitingList: return 'white--text purple darken-4';
        default: return 'grey lighten-1';
      }
    },
    tablesHtmlText(): string {
      return this.r.tabItems.map((ti) => (this.overbookedTabItems?.has(ti.id)
        ? `<span class="error--text">${ti.no}</span>`
        : `${ti.no}`)).join(', ');
    },
  },
  methods: {
    ...mapActions(['sendReservation', 'setReservationFromContextStatusChange']),
    statusButtonOutlined(s: ReservationStatus) {
      return s === ReservationStatus.Planned;
    },
    async statusChanged(status: ReservationStatus) {
      if (status === ReservationStatus.Cancelled) {
        this.confirmCancelReservation = true;
        return;
      }

      const r = cloneModel(this.r);
      r.setStatus(status);
      const ok = await performSaveAction(
        undefined,
        async () => this.sendReservation({ reservation: r }),
      );
      if (ok) this.setReservationFromContextStatusChange(r);
    },
    async cancelReservation(requestRefund?: boolean) {
      const r = cloneModel(this.reservation);
      r.setStatus(ReservationStatus.Cancelled);
      r.isRefundRequested = requestRefund ?? null;
      const ok = await performSaveAction(
        undefined,
        async () => this.sendReservation({ reservation: r }),
      );
      if (ok) this.setReservationFromContextStatusChange(r);
    },
    async markAsRead() {
      const r = cloneModel(this.r);
      r.isUnread = false;
      await performSaveAction(
        undefined,
        async () => this.sendReservation({ reservation: r }),
      );
    },
  },
});
</script>

<style lang="scss" scoped>

  .glr-name {
    min-width: 200px;
    max-width: 200px;
    &.glr-name-compact {
      min-width: 100px;
      max-width: 100px
    }
  }

  .glr-icon-column {
    display: inline-block;
    width: 20px;
    min-width:20px
  }

  .glr-star-compact {
    display: inline-block;
    width: 20px;
  }

  .glr-search-time {
    min-width: 90px;
  }

  .glr-partysize-compact {
    min-width: 50px;
    display:inline-block;
  }

  .glr-chips-compact {
    max-height: 40px;
    min-height: 0px;
    padding-left: 20px!important;
  }

  .glr-note-compact {
    max-height: 40px;
    padding-left: 20px!important;
    display: table;
    table-layout: fixed;
  }

  .glr-status-btn {
    min-width: 160px;
    max-width: 160px;
  }

  .glr-note {
    max-height: 40px;
    overflow: hidden;
  }

  .glr-block-50 {
    min-width: 50px;
    max-width: 50px;
  }

  .glr-block-20 {
    min-width: 20px;
    max-width: 20px;
  }

  .glr-block-125 {
    width: 125px;
    min-width: 125px;
    max-width: 125px;
  }

  .glr-compact-company {
    margin-top: -5px;
    line-height: unset;
  }

  .mh-48 {
    min-height: 48px;
  }

</style>
